import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, ShieldCheck, Link as LinkIcon, CheckCircle, Lock, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async'; 
import axios from 'axios';

const getCurrencySymbol = (code) => {
  switch(code) {
    case 'USD': return '$'; case 'EUR': return '€'; case 'GBP': return '£';
    case 'BTC': return '₿'; case 'ETH': return 'Ξ'; default: return '₺';
  }
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- KASA (PIN) STATE'LERİ ---
  const [pin, setPin] = useState('');
  const [unlockedFile, setUnlockedFile] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  // GÜVENLİ API URL ÜRETİCİ
  const API_URL = process.env.NODE_ENV === 'production' 
    ? 'https://mertt.tr/api' 
    : 'http://localhost:8000/api';

  // 1. ÜRÜN BİLGİSİNİ ÇEKME
  useEffect(() => {
    axios.get(`${API_URL}/products/${productId}`)
      .then((res) => {
        setProduct(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Hata:", err.response?.data || err.message);
        toast.error("Ürün bilgisi çekilemedi.");
        setLoading(false);
        setTimeout(() => navigate('/'), 2000);
      });
  }, [productId, navigate, API_URL]);

  // ==========================================================
  // 2. ÖZEL KOD (HTML/SCRIPT) ÇALIŞTIRICI SİSTEM (ENJEKTÖR)
  // ==========================================================
  useEffect(() => {
    if (product?.details_tr) {
      const container = document.getElementById('custom-html-container');
      if (container) {
        const scriptTags = container.getElementsByTagName('script');
        Array.from(scriptTags).forEach((script) => {
          const newScript = document.createElement('script');
          if (script.src) {
            newScript.src = script.src;
          } else {
            newScript.innerHTML = script.innerHTML;
          }
          document.body.appendChild(newScript);
        });
      }
    }
  }, [product]);

  // --- KASA ŞİFRESİ DOĞRULAMA FONKSİYONU ---
  const handleUnlock = async () => {
    if(!pin) { toast.error("Lütfen şifrenizi girin!"); return; }
    setUnlocking(true);
    try {
        const res = await axios.post(`${API_URL}/products/${product.id}/verify-pin`, { pin });
        setUnlockedFile(res.data.file_url);
        toast.success("Kilit Açıldı! Dosyanızı indirebilirsiniz.");
    } catch (err) {
        toast.error("Hatalı veya geçersiz şifre!");
    } finally {
        setUnlocking(false);
    }
  }

  // BUTON YÖNLENDİRME YARDIMCISI
  const handleButtonClick = (actionType, externalLink) => {
    if (actionType === 'external' && externalLink) {
      window.open(externalLink, '_blank', 'noopener,noreferrer');
    } else {
      // Eğer "internal" (site içi) seçilmişse, sayfanın aşağısındaki detaylara kaydır
      document.getElementById('custom-html-container')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500 mr-3"></div>
      Yükleniyor...
    </div>
  );

  if (!product) return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center">
      <p className="text-xl mb-4 font-bold text-red-500">Ürün bilgisi alınamadı!</p>
      <button onClick={() => navigate('/')} className="px-6 py-2 bg-white text-black rounded-full font-semibold hover:bg-gray-200 transition">Ana Sayfaya Dön</button>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-slate-900 text-white p-6 md:p-12 pt-28">
      
      <Helmet>
        <title>{product.name_tr} | MERTT Future Technology</title>
        <meta name="description" content={product.description_tr} />
        <meta name="keywords" content={product.seo_tags} />
      </Helmet>

      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate(-1)} className="mb-8 flex items-center gap-2 text-gray-400 hover:text-white transition">
          <ArrowLeft size={20} /> Geri Dön
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div initial={{ x: -50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-500"></div>
            <img src={product.image_url || "https://via.placeholder.com/600"} alt={product.name_tr} className="relative w-full rounded-2xl border border-slate-700 object-cover min-h-[400px]" />
          </motion.div>

          <motion.div initial={{ x: 50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="space-y-8">
            <div>
                <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400 mb-2">{product.name_tr}</h1>
                <p className="text-gray-400 text-lg leading-relaxed">{product.description_tr}</p>
            </div>
            
            <div className="bg-slate-800/50 border border-slate-700 p-6 rounded-2xl flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-500 mb-1">Ürün Fiyatı</p>
                    <div className="text-4xl text-cyan-400 font-bold flex items-center gap-2">
                        <span>{product.price ? product.price.toLocaleString('tr-TR') : '0'}</span>
                        <span>{getCurrencySymbol(product.currency)}</span>
                    </div>
                </div>
                <span className="bg-cyan-500/10 text-cyan-400 px-4 py-2 rounded-lg border border-cyan-500/20">{product.currency}</span>
            </div>

            {/* --- V3.0 DİNAMİK ÇİFT BUTON SİSTEMİ --- */}
            <div className="flex flex-col sm:flex-row gap-4">
                
                {/* 1. BUTON (SOL - Genellikle İncele/Demo) */}
                <button 
                  onClick={() => handleButtonClick(product.button_action_type, product.external_link)}
                  className="flex-1 flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 border border-cyan-900/50 text-cyan-400 p-4 rounded-xl font-bold text-lg transition shadow-lg"
                >
                  {product.button_action_type === 'external' ? <ExternalLink size={20} /> : <LinkIcon size={20} />} 
                  {product.button_text_tr || "İncele"}
                </button>

                {/* 2. BUTON (SAĞ - Genellikle Satın Al) */}
                <button 
                  onClick={() => handleButtonClick(product.button2_action_type, product.button2_external_link)}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white p-4 rounded-xl font-bold text-lg transition shadow-lg shadow-cyan-900/50"
                >
                  {product.button2_action_type === 'external' ? <CreditCard size={20} /> : <CheckCircle size={20} />} 
                  {product.button2_text_tr || "Satın Al"}
                </button>

            </div>

            {/* --- GÜVENLİ DİJİTAL KASA --- */}
            {product.has_secure_file && (
                <div className="bg-slate-800/80 border border-slate-700 p-6 rounded-2xl mt-4">
                    <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                        <Lock className="text-cyan-400" size={20} /> Dijital Ürün Kilidi
                    </h3>
                    <p className="text-sm text-gray-400 mb-4">Ürünü satın aldıktan sonra size verilen şifreyi (PIN) girerek dosyanızı indirebilirsiniz.</p>
                    
                    {!unlockedFile ? (
                        <div className="flex flex-col sm:flex-row gap-2">
                            <input 
                                type="text" 
                                placeholder="Şifrenizi Girin (Örn: MERTT-2026)" 
                                className="w-full bg-slate-900/50 text-white border border-cyan-900/50 focus:border-cyan-500 rounded-xl px-4 py-3 outline-none transition" 
                                value={pin}
                                onChange={(e) => setPin(e.target.value)}
                            />
                            <button 
                                onClick={handleUnlock}
                                disabled={unlocking}
                                className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-3 rounded-xl font-bold transition flex items-center justify-center gap-2 whitespace-nowrap"
                            >
                                {unlocking ? 'Açılıyor...' : 'Kilidi Aç'}
                            </button>
                        </div>
                    ) : (
                        <a href={unlockedFile} download target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white py-4 rounded-xl font-bold transition shadow-lg shadow-green-900/50">
                            <Download size={20} /> Dosyayı Şimdi İndir
                        </a>
                    )}
                </div>
            )}

            <div className="flex flex-col gap-3 py-2 border-t border-slate-800 pt-6 mt-6">
              <div className="flex items-center gap-2 text-sm text-gray-400"><ShieldCheck size={18} className="text-green-500" /> %100 Güvenli Ödeme Altyapısı</div>
              <div className="flex items-center gap-2 text-sm text-gray-400"><CheckCircle size={18} className="text-blue-500" /> Anında Teslimat & Destek</div>
            </div>
          </motion.div>
        </div>

        {/* --- DİNAMİK HTML İÇERİK (ADMIN'DEN GELEN) --- */}
        {product.details_tr && (
            <div className="mt-20 pt-10 border-t border-slate-800">
                 <h3 className="text-3xl font-bold mb-8 text-white flex items-center gap-3">
                    <div className="w-1.5 h-8 bg-cyan-500 rounded-full"></div>
                    Ürün Detayları
                 </h3>
                 {/* DİKKAT: id="custom-html-container" EKLENDİ, SCRIPTLER BURADA YAKALANACAK */}
                 <div id="custom-html-container" className="bg-slate-800/30 p-8 rounded-3xl border border-slate-700/50">
                    <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: product.details_tr }} />
                 </div>
            </div>
        )}
      </div>
    </motion.div>
  );
};

export default ProductDetailPage;